import type { CSSProperties } from "react";
import { useState, useRef } from "react";
import { cn } from "../lib/utils";

interface DropZoneProps {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  maxSizeMB?: number;
  label?: string;
  hint?: string;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DropZone({
  onFiles,
  accept,
  multiple = true,
  maxSizeMB,
  label = "Drop files here or click to upload",
  hint,
  disabled = false,
  className,
  style,
}: DropZoneProps) {
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  function validate(files: File[]): File[] {
    if (maxSizeMB) {
      const oversized = files.filter(f => f.size > maxSizeMB * 1024 * 1024);
      if (oversized.length) {
        setError(`File too large: max ${maxSizeMB} MB.`);
        return [];
      }
    }
    setError(null);
    return files;
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files);
    const valid = validate(files);
    if (valid.length) onFiles(multiple ? valid : [valid[0]]);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    const valid = validate(files);
    if (valid.length) onFiles(valid);
    e.target.value = "";
  }

  return (
    <div
      className={cn("dz-root", dragging && "dz-over", disabled && "dz-disabled", className)}
      style={style}
      onDragOver={e => { e.preventDefault(); if (!disabled) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="dz-input"
        onChange={handleChange}
        tabIndex={-1}
      />
      <div className="dz-icon" aria-hidden="true">
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <path d="M14 4v14M8 10l6-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M4 20v2a2 2 0 002 2h16a2 2 0 002-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </div>
      <p className="dz-label">{label}</p>
      {hint && <p className="dz-hint">{hint}</p>}
      {maxSizeMB && <p className="dz-hint">Max size: {formatSize(maxSizeMB * 1024 * 1024)}</p>}
      {error && <p className="dz-error">{error}</p>}
    </div>
  );
}
